import { useState, useEffect } from 'react';
import * as exifr from 'exifr';
import './App.css';
import { db, auth, storage } from './firebase';
import { collection, addDoc, getDocs, query, where, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendPasswordResetEmail,
  deleteUser
} from 'firebase/auth';
import { ref, getDownloadURL, ref as storageRef, deleteObject } from 'firebase/storage';
import { getFestivalsForCoordinates, allFestivals } from './festivalDatabase';
import Privacy from './Privacy';
import Terms from './Terms';
import { processUploadQueue, checkForDuplicates } from './uploadUtils';
import UploadProgress from './UploadProgress';

function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [festivalInput, setFestivalInput] = useState('');
  const [customInput, setCustomInput] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [festivalSuggestions, setFestivalSuggestions] = useState([]);
  const [currentView, setCurrentView] = useState('upload');
  const [selectedFestival, setSelectedFestival] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [yearFilter, setYearFilter] = useState('all');
  const [aftersetIndex, setAftersetIndex] = useState(0);
  const [isAftersetPlus, setIsAftersetPlus] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState('');
  const [currentPage, setCurrentPage] = useState('app');
  const [uploadQueue, setUploadQueue] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [totalSaved, setTotalSaved] = useState(0);
  const [duplicateCount, setDuplicateCount] = useState(0);

  const FREE_FESTIVAL_LIMIT = 3;
  const FREE_MEMORY_LIMIT = 500;

  // Check if user has premium access from Firestore
  useEffect(() => {
    const checkPremiumStatus = async () => {
      if (user && user.email) {
        try {
          const premiumDoc = await getDocs(
            query(collection(db, 'premium_users'), where('email', '==', user.email.toLowerCase()))
          );
          
          if (!premiumDoc.empty) {
            const userData = premiumDoc.docs[0].data();
            const isPremium = userData.status === 'active';
            setIsAftersetPlus(isPremium);
            if (isPremium) {
              console.log('✅ AFTERSET+ user detected:', user.email);
            }
          } else {
            setIsAftersetPlus(false);
          }
        } catch (error) {
          console.error('Error checking premium status:', error);
          setIsAftersetPlus(false);
        }
      } else {
        setIsAftersetPlus(false);
      }
    };

    checkPremiumStatus();
  }, [user]);

  const canAddFestival = () => {
    if (isAftersetPlus) return true;
    const festivalGroups = getFestivalGroups();
    return Object.keys(festivalGroups).length < FREE_FESTIVAL_LIMIT;
  };

  const handleUpgrade = (plan) => {
    if (!user) {
      alert('Please log in to upgrade');
      return;
    }

    const paymentLink = plan === 'monthly' 
      ? process.env.REACT_APP_STRIPE_MONTHLY_PAYMENT_LINK
      : process.env.REACT_APP_STRIPE_YEARLY_PAYMENT_LINK;

    window.location.href = paymentLink;
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthLoading(false);
      
      if (currentUser) {
        loadUserMemories(currentUser.uid);
        setShowAuthModal(false);
      } else {
        setFiles([]);
        setShowAuthModal(true);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    
    try {
      await signInWithEmailAndPassword(auth, email, password);
      setEmail('');
      setPassword('');
    } catch (error) {
      console.error('Login error:', error);
      if (error.code === 'auth/user-not-found') {
        setAuthError('No account found with this email.');
      } else if (error.code === 'auth/wrong-password') {
        setAuthError('Incorrect password.');
      } else if (error.code === 'auth/invalid-email') {
        setAuthError('Invalid email address.');
      } else {
        setAuthError('Login failed. Please try again.');
      }
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setAuthError('');
    
    if (password.length < 6) {
      setAuthError('Password must be at least 6 characters.');
      return;
    }
    
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      setEmail('');
      setPassword('');
    } catch (error) {
      console.error('Signup error:', error);
      if (error.code === 'auth/email-already-in-use') {
        setAuthError('Email already in use. Try logging in.');
      } else if (error.code === 'auth/invalid-email') {
        setAuthError('Invalid email address.');
      } else if (error.code === 'auth/weak-password') {
        setAuthError('Password is too weak.');
      } else {
        setAuthError('Signup failed. Please try again.');
      }
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setFiles([]);
      setCurrentView('upload');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      setAuthError('Enter your email to reset password.');
      return;
    }
    
    try {
      await sendPasswordResetEmail(auth, email);
      alert('Password reset email sent! Check your inbox.');
    } catch (error) {
      console.error('Password reset error:', error);
      setAuthError('Failed to send reset email.');
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    
    const confirmDelete = window.confirm(
      '⚠️ DELETE ACCOUNT?\n\nThis will permanently delete:\n• Your account\n• All your memories\n• All your festival data\n\nThis CANNOT be undone!\n\nType "DELETE" in the next prompt to confirm.'
    );
    
    if (!confirmDelete) return;
    
    const confirmText = window.prompt('Type DELETE to confirm account deletion:');
    
    if (confirmText !== 'DELETE') {
      alert('Account deletion cancelled.');
      return;
    }
    
    try {
      setLoading(true);
      
      const q = query(collection(db, 'memories'), where('userId', '==', user.uid));
      const querySnapshot = await getDocs(q);
      
      console.log(`Deleting ${querySnapshot.docs.length} memories...`);
      
      for (const docSnapshot of querySnapshot.docs) {
        const memoryData = docSnapshot.data();
        
        await deleteDoc(doc(db, 'memories', docSnapshot.id));
        
        if (memoryData.url && memoryData.url.includes('firebasestorage')) {
          try {
            const urlParts = memoryData.url.split('/o/')[1];
            const filePath = decodeURIComponent(urlParts.split('?')[0]);
            const fileRef = storageRef(storage, filePath);
            await deleteObject(fileRef);
          } catch (error) {
            console.log('Could not delete file from storage:', error);
          }
        }
      }
      
      await deleteUser(user);
      
      alert('Account deleted successfully. Goodbye! 👋');
      
      setFiles([]);
      setLoading(false);
      
    } catch (error) {
      console.error('Error deleting account:', error);
      setLoading(false);
      
      if (error.code === 'auth/requires-recent-login') {
        alert('For security, please log out and log back in, then try deleting your account again.');
      } else {
        alert('Failed to delete account. Please try again or contact support.');
      }
    }
  };

  useEffect(() => {
    if (currentView === 'afterset') {
      const handleKeyPress = (e) => {
        if (e.key === 'ArrowRight') {
          setAftersetIndex(prev => {
            const festivalGroups = getFestivalGroups();
            const festivalFiles = festivalGroups[selectedFestival];
            const sortedFiles = [...festivalFiles].sort((a, b) => {
              if (a.date === 'Untagged') return 1;
              if (b.date === 'Untagged') return -1;
              return new Date(a.date) - new Date(b.date);
            });
            return prev < sortedFiles.length - 1 ? prev + 1 : prev;
          });
        }
        if (e.key === 'ArrowLeft') {
          setAftersetIndex(prev => prev > 0 ? prev - 1 : prev);
        }
        if (e.key === 'Escape') {
          setCurrentView('list');
        }
      };
      
      window.addEventListener('keydown', handleKeyPress);
      return () => window.removeEventListener('keydown', handleKeyPress);
    }
  }, [currentView, selectedFestival]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUserMemories = async (userId) => {
    try {
      const q = query(collection(db, 'memories'), where('userId', '==', userId));
      const querySnapshot = await getDocs(q);
      
      const loadedMemories = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setFiles(loadedMemories);
      console.log('✅ Loaded', loadedMemories.length, 'memories from Firebase');
    } catch (error) {
      console.error('❌ Error loading memories:', error);
    }
  };

  // =====================================================
  // UPLOAD HANDLER — compressed, resumable, queued, deduped
  // =====================================================
  const handleFileUpload = async (event) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    const uploadedFiles = Array.from(event.target.files);
    
    if (uploadedFiles.length === 0) return;

    // Mobile batch limit — prevent browser crashes on mobile devices
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile && uploadedFiles.length > 3) {
      alert(`📱 On mobile, please select up to 3 files at a time for the best experience.\n\nYou selected ${uploadedFiles.length}. You can upload more right after this batch finishes!`);
      event.target.value = '';
      return;
    }

    // Check for duplicates BEFORE uploading
    const { newFiles, duplicates } = await checkForDuplicates(uploadedFiles, user.uid);
    
    setDuplicateCount(duplicates.length);

    if (duplicates.length > 0) {
      console.log(`🔁 Skipping ${duplicates.length} duplicates:`, duplicates);
    }

    // If ALL files are duplicates, let the user know and stop
    if (newFiles.length === 0) {
      alert(`All ${duplicates.length} file${duplicates.length > 1 ? 's have' : ' has'} already been uploaded! No duplicates were added.`);
      event.target.value = '';
      return;
    }

    if (!isAftersetPlus && files.length + newFiles.length > FREE_MEMORY_LIMIT) {
      setUpgradeReason('memory');
      setShowUpgradeModal(true);
      return;
    }
    
    // Initialize the upload queue UI (only for new files)
    const initialQueue = newFiles.map(file => ({
      name: file.name,
      size: file.size,
      type: file.type.startsWith('video') ? 'video' : 'image',
      stage: 'queued',
      progress: 0,
    }));
    
    setUploadQueue(initialQueue);
    setIsUploading(true);
    setTotalSaved(0);
    setLoading(true);
    
    console.log(`📸 Starting upload of ${newFiles.length} files (${duplicates.length} duplicates skipped)...`);
    
    await processUploadQueue(newFiles, user.uid, {
      onFileStart: (index, file, stage) => {
        setUploadQueue(prev => {
          const updated = [...prev];
          updated[index] = { ...updated[index], stage, progress: 0 };
          return updated;
        });
      },
      
      onFileProgress: (index, file, progress, stage) => {
        setUploadQueue(prev => {
          const updated = [...prev];
          updated[index] = { ...updated[index], progress: Math.round(progress), stage };
          return updated;
        });
      },
      
      onFileComplete: async (index, file, result) => {
        // Track compression savings
        const saved = result.originalSize - result.compressedSize;
        if (saved > 0) {
          setTotalSaved(prev => prev + saved);
        }
        
        // Update queue UI
        setUploadQueue(prev => {
          const updated = [...prev];
          updated[index] = { ...updated[index], stage: 'complete', progress: 100 };
          return updated;
        });
        
        // Extract metadata & save to Firestore
        const originalFile = result.originalFile;
        let metadata = null;
        
        if (originalFile.type.startsWith('image')) {
          try {
            metadata = await exifr.parse(originalFile);
          } catch (error) {
            console.log('Could not read EXIF data:', error);
          }
        }

        let suggestedFestival = null;
        if (metadata?.latitude && metadata?.longitude && metadata?.DateTimeOriginal) {
          try {
            const { detectFestival } = await import('./festivalDetection');
            suggestedFestival = await detectFestival(
              metadata.latitude,
              metadata.longitude,
              metadata.DateTimeOriginal
            );
          } catch (e) {
            console.log('Festival detection failed:', e);
          }
        }

        const memoryData = {
          userId: user.uid,
          name: originalFile.name,
          size: (result.compressedSize / 1024 / 1024).toFixed(2),
          type: originalFile.type.startsWith('image') ? 'image' : 'video',
          date: metadata?.DateTimeOriginal ? metadata.DateTimeOriginal.toISOString() : 'Untagged',
          location: metadata?.latitude && metadata?.longitude 
            ? `${metadata.latitude.toFixed(4)}, ${metadata.longitude.toFixed(4)}`
            : 'No location data',
          festival: suggestedFestival,
          aiSuggested: !!suggestedFestival,
          url: result.url,
          createdAt: new Date().toISOString()
        };

        try {
          const docRef = await addDoc(collection(db, 'memories'), memoryData);
          
          // Update files state incrementally — user sees memories appear in real-time
          setFiles(prev => [...prev, { id: docRef.id, ...memoryData }]);
          
        } catch (error) {
          console.error('❌ Error saving to Firestore:', error);
        }
      },
      
      onFileError: (index, file, error) => {
        setUploadQueue(prev => {
          const updated = [...prev];
          updated[index] = { ...updated[index], stage: 'error', progress: 0 };
          return updated;
        });
      },
      
      onQueueComplete: (results) => {
        const successful = results.filter(r => r && !r.error).length;
        const failed = results.filter(r => r && r.error).length;
        console.log(`✅ Upload complete! ${successful} succeeded, ${failed} failed`);
        
        // Keep the progress modal visible for 2 seconds after completion
        setTimeout(() => {
          setIsUploading(false);
          setUploadQueue([]);
          setLoading(false);
          setDuplicateCount(0);
        }, 2000);
      }
    });
    
    // Reset file input so same files can be re-selected if needed
    event.target.value = '';
  };

  const triggerFileInput = () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    document.getElementById('fileInput').click();
  };

  const startEditing = (index) => {
    setEditingIndex(index);
    const file = files[index];
    setFestivalInput(file.festival || '');
    setShowCustomInput(false);
    setCustomInput('');
    
    if (file.location && file.location !== 'No location data') {
      const [lat, lon] = file.location.split(', ').map(coord => parseFloat(coord));
      const regionalSuggestions = getFestivalsForCoordinates(lat, lon);
      setFestivalSuggestions(regionalSuggestions);
      console.log('🎪 Found', regionalSuggestions.length, 'regional festival suggestions');
    } else {
      setFestivalSuggestions([]);
      console.log('🎪 Showing all festivals (no GPS data)');
    }
  };

  const saveFestival = async (index) => {
    const updatedFiles = [...files];
    const newFestival = festivalInput;
    const currentFestival = updatedFiles[index].festival;
    
    if (!currentFestival && newFestival) {
      const existingFestivals = Object.keys(getFestivalGroups());
      if (!existingFestivals.includes(newFestival) && !canAddFestival()) {
        setUpgradeReason('festival');
        setShowUpgradeModal(true);
        setEditingIndex(null);
        setFestivalInput('');
        setFestivalSuggestions([]);
        setShowCustomInput(false);
        setCustomInput('');
        return;
      }
    }
    
    updatedFiles[index].festival = festivalInput;
    setFiles(updatedFiles);
    setEditingIndex(null);
    setFestivalInput('');
    setFestivalSuggestions([]);
    setShowCustomInput(false);
    setCustomInput('');

    // Save to Firestore so it persists
    const memory = updatedFiles[index];
    if (memory.id) {
      try {
        await updateDoc(doc(db, 'memories', memory.id), {
          festival: memory.festival || null,
          aiSuggested: false
        });
        console.log('✅ Festival tag saved to Firestore:', memory.festival);
      } catch (error) {
        console.error('❌ Failed to save festival tag:', error);
      }
    }
  };

  const deleteMemory = async (index) => {
    const memoryToDelete = files[index];
    
    if (window.confirm('Delete this memory? This cannot be undone.')) {
      try {
        if (memoryToDelete.id) {
          await deleteDoc(doc(db, 'memories', memoryToDelete.id));
          console.log('✅ Deleted from Firestore:', memoryToDelete.id);
        }
        
        if (memoryToDelete.url && memoryToDelete.url.includes('firebasestorage')) {
          try {
            const urlParts = memoryToDelete.url.split('/o/')[1];
            const filePath = decodeURIComponent(urlParts.split('?')[0]);
            const fileRef = storageRef(storage, filePath);
            await deleteObject(fileRef);
            console.log('✅ Deleted from Storage:', filePath);
          } catch (storageError) {
            console.log('⚠️ Could not delete from storage:', storageError);
          }
        }
        
        const updatedFiles = files.filter((_, i) => i !== index);
        setFiles(updatedFiles);
        
        console.log('✅ Memory deleted successfully:', memoryToDelete.name);
      } catch (error) {
        console.error('❌ Error deleting memory:', error);
        alert('Failed to delete memory. Please try again.');
      }
    }
  };

  const cancelEditing = () => {
    setEditingIndex(null);
    setFestivalInput('');
    setFestivalSuggestions([]);
    setShowCustomInput(false);
    setCustomInput('');
  };

  const goToFestivalList = () => {
    setCurrentView('list');
    setYearFilter('all');
  };

  const goToFestivalView = (festivalName) => {
    setSelectedFestival(festivalName);
    setCurrentView('festival');
    
    const festivalFiles = festivalGroups[festivalName];
    const yearGroups = {};
    
    festivalFiles.forEach(file => {
      let year = 'Untagged';
      if (file.date && file.date !== 'Untagged') {
        const dateObj = new Date(file.date);
        if (!isNaN(dateObj.getTime())) {
          year = dateObj.getFullYear().toString();
        }
      }
      
      if (!yearGroups[year]) {
        yearGroups[year] = [];
      }
      yearGroups[year].push(file);
    });

    const years = Object.keys(yearGroups).sort().reverse();
    setSelectedYear(years[0]);
  };

  const openAftersetAtIndex = (festivalName, memoryIndex) => {
    setSelectedFestival(festivalName);
    setAftersetIndex(memoryIndex);
    setCurrentView('afterset');
  };

  const goBackToUpload = () => {
    setCurrentView('upload');
    setSelectedFestival(null);
  };

  const getFestivalGroups = () => {
    const groups = {};
    files.forEach(file => {
      if (file.festival) {
        if (!groups[file.festival]) {
          groups[file.festival] = [];
        }
        groups[file.festival].push(file);
      }
    });
    return groups;
  };

  const getAvailableYears = () => {
    const years = new Set();
    files.forEach(file => {
      if (!file.festival) return;
      
      if (file.date && file.date !== 'Untagged') {
        const dateObj = new Date(file.date);
        if (!isNaN(dateObj.getTime())) {
          years.add(dateObj.getFullYear().toString());
        }
      } else {
        years.add('Untagged');
      }
    });
    return Array.from(years).sort().reverse();
  };

  const getFilteredFestivals = () => {
    if (yearFilter === 'all') {
      return festivalGroups;
    }

    const filtered = {};
    Object.entries(festivalGroups).forEach(([festivalName, festivalFiles]) => {
      const filesInYear = festivalFiles.filter(file => {
        if (yearFilter === 'Untagged') {
          return file.date === 'Untagged';
        }
        
        if (file.date && file.date !== 'Untagged') {
          const dateObj = new Date(file.date);
          if (!isNaN(dateObj.getTime())) {
            return dateObj.getFullYear().toString() === yearFilter;
          }
        }
        return false;
      });

      if (filesInYear.length > 0) {
        filtered[festivalName] = filesInYear;
      }
    });

    return filtered;
  };

  const getSingleFestivalYearGroups = (festivalFiles) => {
    const yearGroups = {};
    
    festivalFiles.forEach(file => {
      let year = 'Untagged';
      if (file.date && file.date !== 'Untagged') {
        const dateObj = new Date(file.date);
        if (!isNaN(dateObj.getTime())) {
          year = dateObj.getFullYear().toString();
        }
      }
      
      if (!yearGroups[year]) {
        yearGroups[year] = [];
      }
      yearGroups[year].push(file);
    });

    return yearGroups;
  };

  const festivalGroups = getFestivalGroups();
  const taggedCount = files.filter(f => f.festival).length;
  const untaggedCount = files.filter(f => !f.festival).length;
  const availableYears = getAvailableYears();
  const filteredFestivals = getFilteredFestivals();

  if (currentPage === 'privacy') {
    return (
      <>
        <Privacy />
        <footer className="app-footer">
          <p>© 2026 AFTERSET. All rights reserved.</p>
          <div className="footer-links">
            <button onClick={() => setCurrentPage('app')} className="footer-link">
              Back to App
            </button>
            <span className="footer-divider">•</span>
            <button onClick={() => setCurrentPage('terms')} className="footer-link">
              Terms of Service
            </button>
            <span className="footer-divider">•</span>
            <a href="mailto:content.nr17@gmail.com" className="footer-link">
              Contact
            </a>
          </div>
        </footer>
      </>
    );
  }

  if (currentPage === 'terms') {
    return (
      <>
        <Terms />
        <footer className="app-footer">
          <p>© 2026 AFTERSET. All rights reserved.</p>
          <div className="footer-links">
            <button onClick={() => setCurrentPage('app')} className="footer-link">
              Back to App
            </button>
            <span className="footer-divider">•</span>
            <button onClick={() => setCurrentPage('privacy')} className="footer-link">
              Privacy Policy
            </button>
            <span className="footer-divider">•</span>
            <a href="mailto:content.nr17@gmail.com" className="footer-link">
              Contact
            </a>
          </div>
        </footer>
      </>
    );
  }

  if (authLoading) {
    return (
      <div className="App" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #0a0a0a 0%, #1a0a2e 100%)',
        color: 'white',
        fontSize: '24px'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <>
      <div className="App">
        {user && (
          <button className="signout-btn" onClick={handleLogout}>
            Sign Out
          </button>
        )}

        <UploadProgress 
          uploadQueue={uploadQueue} 
          isUploading={isUploading} 
          totalSaved={totalSaved}
          duplicateCount={duplicateCount}
        />

        {showAuthModal && (
          <div className="auth-modal-overlay">
            <div className="auth-modal">
              <div className="auth-logo">
                <div className="sunset-logo-small">
                  <svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <linearGradient id="sunsetGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" style={{stopColor: '#ff6b35', stopOpacity: 1}} />
                        <stop offset="50%" style={{stopColor: '#ff006e', stopOpacity: 1}} />
                        <stop offset="100%" style={{stopColor: '#b537f2', stopOpacity: 1}} />
                      </linearGradient>
                    </defs>
                    <path d="M 25 80 A 35 35 0 0 1 95 80 Z" fill="url(#sunsetGradient)" opacity="0.95" />
                    <line x1="60" y1="35" x2="60" y2="18" stroke="#ff6b35" strokeWidth="3" opacity="0.8" strokeLinecap="round" />
                    <line x1="35" y1="50" x2="23" y2="38" stroke="#ff6b35" strokeWidth="3" opacity="0.8" strokeLinecap="round" />
                    <line x1="85" y1="50" x2="97" y2="38" stroke="#ff6b35" strokeWidth="3" opacity="0.8" strokeLinecap="round" />
                    <line x1="15" y1="80" x2="105" y2="80" stroke="#00f3ff" strokeWidth="4" strokeLinecap="round" opacity="0.95" />
                  </svg>
                </div>
                <h2>AFTERSET</h2>
                <p>Where Your Festival Lives On</p>
              </div>

              <div className="auth-tabs">
                <button 
                  className={`auth-tab ${authMode === 'login' ? 'active' : ''}`}
                  onClick={() => {
                    setAuthMode('login');
                    setAuthError('');
                  }}
                >
                  Log In
                </button>
                <button 
                  className={`auth-tab ${authMode === 'signup' ? 'active' : ''}`}
                  onClick={() => {
                    setAuthMode('signup');
                    setAuthError('');
                  }}
                >
                  Sign Up
                </button>
              </div>

              <form onSubmit={authMode === 'login' ? handleLogin : handleSignup}>
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="auth-input"
                />
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="auth-input"
                />

                {authError && <p className="auth-error">{authError}</p>}

                <button type="submit" className="auth-submit-btn">
                  {authMode === 'login' ? 'Log In' : 'Sign Up'}
                </button>
              </form>

              {authMode === 'login' && (
                <button 
                  className="auth-forgot-password" 
                  onClick={handlePasswordReset}
                >
                  Forgot Password?
                </button>
              )}
            </div>
          </div>
        )}

        {showUpgradeModal && (
          <div className="upgrade-modal-overlay" onClick={() => setShowUpgradeModal(false)}>
            <div className="upgrade-modal" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setShowUpgradeModal(false)}>✕</button>
              
              <div className="modal-icon">🚀</div>
              
              <h2>Upgrade to AFTERSET+</h2>
              
              {upgradeReason === 'memory' && (
                <p className="modal-message">
                  You've reached the free tier limit of <strong>500 memories</strong>. 
                  Upgrade to AFTERSET+ for unlimited storage!
                </p>
              )}
              
              {upgradeReason === 'festival' && (
                <p className="modal-message">
                  You've reached the free tier limit of <strong>3 festivals</strong>. 
                  Upgrade to AFTERSET+ for unlimited festivals!
                </p>
              )}
              
              <div className="upgrade-features">
                <div className="feature">
                  <span className="feature-icon">🎪</span>
                  <span>Unlimited Festivals</span>
                </div>
                <div className="feature">
                  <span className="feature-icon">📸</span>
                  <span>Unlimited Memories</span>
                </div>
                <div className="feature">
                  <span className="feature-icon">🎬</span>
                  <span>Afterset View</span>
                </div>
                <div className="feature">
                  <span className="feature-icon">📅</span>
                  <span>Browse by Year</span>
                </div>
              </div>
              
              <div className="upgrade-pricing">
                <div 
                  className="price-option" 
                  onClick={() => handleUpgrade('monthly')}
                  style={{cursor: 'pointer'}}
                >
                  <p className="price">$4.99/month</p>
                  <p className="price-sub">Billed monthly</p>
                </div>
                <div className="price-divider">or</div>
                <div 
                  className="price-option price-option-best"
                  onClick={() => handleUpgrade('yearly')}
                  style={{cursor: 'pointer'}}
                >
                  <span className="best-value-badge">Best Value</span>
                  <p className="price">$49.99/year</p>
                  <p className="price-sub">Save $10/year</p>
                </div>
              </div>
              
              <p className="modal-message" style={{marginTop: '20px', fontSize: '13px', opacity: 0.8}}>
                After purchasing, email <strong>content.nr17@gmail.com</strong> with your payment confirmation to activate AFTERSET+
              </p>
              
              <button className="modal-cancel" onClick={() => setShowUpgradeModal(false)}>
                Maybe Later
              </button>
            </div>
          </div>
        )}

        {currentView === 'upload' && (
          <>
            <header className="App-header">
              <div className="logo-container">
                <div className="sunset-logo">
                  <svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <linearGradient id="sunsetGradient2" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" style={{stopColor: '#ff6b35', stopOpacity: 1}} />
                        <stop offset="50%" style={{stopColor: '#ff006e', stopOpacity: 1}} />
                        <stop offset="100%" style={{stopColor: '#b537f2', stopOpacity: 1}} />
                      </linearGradient>
                    </defs>
                    <path d="M 25 80 A 35 35 0 0 1 95 80 Z" fill="url(#sunsetGradient2)" opacity="0.95" />
                    <line x1="60" y1="35" x2="60" y2="18" stroke="#ff6b35" strokeWidth="3" opacity="0.8" strokeLinecap="round" />
                    <line x1="35" y1="50" x2="23" y2="38" stroke="#ff6b35" strokeWidth="3" opacity="0.8" strokeLinecap="round" />
                    <line x1="85" y1="50" x2="97" y2="38" stroke="#ff6b35" strokeWidth="3" opacity="0.8" strokeLinecap="round" />
                    <line x1="15" y1="80" x2="105" y2="80" stroke="#00f3ff" strokeWidth="4" strokeLinecap="round" opacity="0.95" />
                  </svg>
                </div>
              </div>
              <h1>AFTERSET</h1>
              <p>Where Your Festival Lives On</p>
              {isAftersetPlus && (
                <p style={{color: '#00f3ff', fontSize: '14px', marginTop: '-10px', fontWeight: '600'}}>
                  ⭐ AFTERSET+ ACTIVE
                </p>
              )}
              <button className="cta-button" onClick={triggerFileInput} disabled={isUploading}>
                {isUploading ? 'Upload in Progress...' : 'Upload Memories'}
              </button>
              {taggedCount > 0 && (
                <button className="view-festivals-btn" onClick={goToFestivalList}>
                  View My Festivals ({Object.keys(festivalGroups).length})
                </button>
              )}
              <button 
                className="delete-account-btn" 
                onClick={handleDeleteAccount}
                style={{
                  background: 'rgba(255, 107, 107, 0.2)',
                  border: '2px solid #ff6b6b',
                  color: '#ff6b6b',
                  padding: '12px 30px',
                  fontSize: '14px',
                  fontWeight: '600',
                  borderRadius: '50px',
                  cursor: 'pointer',
                  marginTop: '20px',
                  transition: 'all 0.3s ease',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = 'rgba(255, 107, 107, 0.3)';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'rgba(255, 107, 107, 0.2)';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                🗑️ Delete Account
              </button>
              <input
                id="fileInput"
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
              <div className="crowd-silhouette"></div>
            </header>

            {files.length > 0 && (
              <div className="files-grid">
                <div className="info-banner">
                  📸 Heads up: Memories without timestamps or locations will show as "Untagged." Just tap the festival tag and pick from the dropdown to organize them!
                </div>
                <h2>Your Festival Memories ({files.length})</h2>
                {untaggedCount > 0 && (
                  <p className="tag-reminder">⚠️ {untaggedCount} memories need to be tagged</p>
                )}
                <div className="grid">
                  {files.map((fileData, index) => (
                    <div key={index} className="file-card">
                      {fileData.type === 'image' ? (
                        <img src={fileData.url} alt={fileData.name} />
                      ) : (
                        <video src={fileData.url} controls />
                      )}
                      <div className="file-info">
                        <p className="file-name">{fileData.name}</p>
                        <p className="file-meta">📅 {fileData.date?.toString().split('T')[0] || fileData.date}</p>
                        <p className="file-meta">📍 {fileData.location}</p>
                        
                        {editingIndex === index ? (
                          <div className="festival-edit">
                            <select 
                              value={festivalInput}
                              onChange={(e) => {
                                if (e.target.value === '__custom__') {
                                  setShowCustomInput(true);
                                  setCustomInput('');
                                  setFestivalInput('');
                                } else {
                                  setShowCustomInput(false);
                                  setFestivalInput(e.target.value);
                                }
                              }}
                              className="festival-dropdown"
                            >
                              <option value="">Select a festival...</option>
                              {fileData.aiSuggested && fileData.festival && (
                                <option value={fileData.festival}>🤖 {fileData.festival} (AI Suggestion)</option>
                              )}
                              {festivalSuggestions.length > 0 && fileData.location && fileData.location !== 'No location data' && (
                                <optgroup label="📍 Festivals Near This Location">
                                  {festivalSuggestions.map((festival, i) => (
                                    <option key={i} value={festival}>{festival}</option>
                                  ))}
                                </optgroup>
                              )}
                              <optgroup label="🎪 All Festivals (A-Z)">
                                {allFestivals.map((festival, i) => (
                                  <option key={`all-${i}`} value={festival}>{festival}</option>
                                ))}
                              </optgroup>
                              <option value="__custom__">✏️ Don't See Yours? (Custom)</option>
                            </select>
                            
                            {showCustomInput && (
                              <input
                                type="text"
                                value={customInput}
                                onChange={(e) => {
                                  setCustomInput(e.target.value);
                                  setFestivalInput(e.target.value);
                                }}
                                placeholder="Enter festival name"
                                className="festival-input"
                                autoFocus
                              />
                            )}
                            
                            <div className="festival-buttons">
                              <button onClick={() => saveFestival(index)} className="save-btn">Save</button>
                              <button onClick={cancelEditing} className="cancel-btn">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <p className="file-meta festival-tag" onClick={() => startEditing(index)}>
                            {fileData.aiSuggested && fileData.festival ? '🤖 ' : '🎪 '}
                            {fileData.festival || 'Click to tag festival'}
                            {fileData.aiSuggested && <span className="ai-badge">AI</span>}
                          </p>
                        )}
                        
                        <p className="file-size">{fileData.size} MB</p>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteMemory(index);
                          }} 
                          className="delete-btn"
                          title="Delete memory"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {currentView === 'list' && (
          <div className="festival-list-view">
            <button className="back-btn" onClick={goBackToUpload}>← Back</button>
            <h1>My Festivals</h1>
            
            <div className="filter-section">
              <label htmlFor="year-filter">Filter by Year: </label>
              <select 
                id="year-filter"
                value={yearFilter} 
                onChange={(e) => setYearFilter(e.target.value)}
                className="year-filter-dropdown"
              >
                <option value="all">All Years</option>
                {availableYears.map(year => (
                  <option key={year} value={year}>
                    {year === 'Untagged' ? 'Untagged' : year}
                  </option>
                ))}
              </select>
            </div>

            <div className="festival-cards">
              {Object.entries(filteredFestivals).map(([festivalName, festivalFiles]) => (
                <div key={festivalName} className="festival-card" onClick={() => goToFestivalView(festivalName)}>
                  <div className="festival-preview">
                    {festivalFiles[0].type === 'image' ? (
                      <img src={festivalFiles[0].url} alt={festivalName} />
                    ) : (
                      <video src={festivalFiles[0].url} />
                    )}
                  </div>
                  <div className="festival-card-info">
                    <h3>{festivalName}</h3>
                    <p>{festivalFiles.length} memories</p>
                    <button 
                      className="afterset-view-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFestival(festivalName);
                        setAftersetIndex(0);
                        setCurrentView('afterset');
                      }}
                    >
                      🎬 View Afterset
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {Object.keys(filteredFestivals).length === 0 && (
              <p className="no-festivals">No festivals found for {yearFilter === 'Untagged' ? 'Untagged' : yearFilter}</p>
            )}
          </div>
        )}

        {currentView === 'afterset' && selectedFestival && (() => {
          const festivalFiles = festivalGroups[selectedFestival];
          const sortedFiles = [...festivalFiles].sort((a, b) => {
            if (a.date === 'Untagged') return 1;
            if (b.date === 'Untagged') return -1;
            return new Date(a.date) - new Date(b.date);
          });
          
          const currentMemory = sortedFiles[aftersetIndex];
          
          const nextMemory = () => {
            if (aftersetIndex < sortedFiles.length - 1) {
              setAftersetIndex(aftersetIndex + 1);
            }
          };
          
          const prevMemory = () => {
            if (aftersetIndex > 0) {
              setAftersetIndex(aftersetIndex - 1);
            }
          };
          
          return (
            <div className="afterset-view">
              <div className="afterset-header">
                <button 
                  className="afterset-close-btn" 
                  onClick={() => setCurrentView('list')}
                >
                  ✕ Close
                </button>
                <div className="afterset-title">
                  <h1>{selectedFestival}</h1>
                  <p className="afterset-counter">
                    Memory {aftersetIndex + 1} of {sortedFiles.length}
                  </p>
                </div>
              </div>
              
              <div className="afterset-content">
                {currentMemory.type === 'image' ? (
                  <img 
                    src={currentMemory.url} 
                    alt={currentMemory.name}
                    className="afterset-media"
                  />
                ) : (
                  <video 
                    src={currentMemory.url} 
                    controls
                    autoPlay
                    className="afterset-media"
                  />
                )}
                
                {aftersetIndex > 0 && (
                  <button className="afterset-nav afterset-nav-prev" onClick={prevMemory}>
                    ‹
                  </button>
                )}
                {aftersetIndex < sortedFiles.length - 1 && (
                  <button className="afterset-nav afterset-nav-next" onClick={nextMemory}>
                    ›
                  </button>
                )}
              </div>
              
              <div className="afterset-footer">
                <p className="afterset-date">
                  📅 {currentMemory.date !== 'Untagged' 
                    ? new Date(currentMemory.date).toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })
                    : 'Date Untagged'}
                </p>
                <p className="afterset-location">
                  📍 {currentMemory.location}
                </p>
              </div>
              
              <div className="afterset-progress-bar">
                <div 
                  className="afterset-progress-fill"
                  style={{ width: `${((aftersetIndex + 1) / sortedFiles.length) * 100}%` }}
                />
              </div>
            </div>
          );
        })()}

        {currentView === 'festival' && selectedFestival && (() => {
          const festivalFiles = festivalGroups[selectedFestival];
          const sortedFiles = [...festivalFiles].sort((a, b) => {
            if (a.date === 'Untagged') return 1;
            if (b.date === 'Untagged') return -1;
            return new Date(a.date) - new Date(b.date);
          });
          const yearGroups = getSingleFestivalYearGroups(festivalFiles);
          const years = Object.keys(yearGroups).sort().reverse();

          return (
            <div className="single-festival-view">
              <button className="back-btn" onClick={() => setCurrentView('list')}>← Back to Festivals</button>
              <h1>{selectedFestival}</h1>
              <p className="festival-count">{festivalFiles.length} memories</p>
              
              {years.length > 1 && (
                <div className="year-tabs">
                  {years.map(year => (
                    <button
                      key={year}
                      className={`year-tab ${selectedYear === year ? 'active' : ''}`}
                      onClick={() => setSelectedYear(year)}
                      title={year === 'Untagged' ? 'Photos without date info (downloaded/shared images)' : ''}
                    >
                      {year === 'Untagged' ? '📅 Untagged' : year} ({yearGroups[year].length})
                    </button>
                  ))}
                </div>
              )}

              <div className="grid">
                {yearGroups[selectedYear]?.map((fileData, globalIndex) => {
                  const memoryIndex = sortedFiles.findIndex(f => f.id === fileData.id);
                  return (
                    <div 
                      key={globalIndex} 
                      className="file-card clickable-memory"
                      onClick={() => openAftersetAtIndex(selectedFestival, memoryIndex)}
                    >
                      {fileData.type === 'image' ? (
                        <img src={fileData.url} alt={fileData.name} />
                      ) : (
                        <video src={fileData.url} />
                      )}
                      <div className="file-info">
                        <p className="file-name">{fileData.name}</p>
                        <p className="file-meta">📅 {fileData.date?.toString().split('T')[0] || fileData.date}</p>
                        <p className="file-meta">📍 {fileData.location}</p>
                        <p className="file-size">{fileData.size} MB</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>

      <footer className="app-footer">
        <p>© 2026 AFTERSET. All rights reserved.</p>
        <div className="footer-links">
          <button onClick={() => setCurrentPage('privacy')} className="footer-link">
            Privacy Policy
          </button>
          <span className="footer-divider">•</span>
          <button onClick={() => setCurrentPage('terms')} className="footer-link">
            Terms of Service
          </button>
          <span className="footer-divider">•</span>
          <a href="mailto:content.nr17@gmail.com" className="footer-link">
            Contact
          </a>
        </div>
      </footer>
    </>
  );
}

export default App;